import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  GroupLayout,
  LayoutProps,
  RankedTester,
  rankWith,
  uiTypeIs,
} from "@jsonforms/core";
import { withJsonFormsLayoutProps } from "@jsonforms/react";
import isEmpty from "lodash/isEmpty";
import React, { FunctionComponent } from "react";
import { renderChildren } from "./util";

export const groupTester: RankedTester = rankWith(4, uiTypeIs("Group"));

interface GroupLayoutOptions {
  /** If true, the group will be rendered as a collapsible accordion */
  collapsible?: boolean;
  /** If true, the group will be expanded by default (only applies when collapsible is true) */
  defaultExpanded?: boolean;
}

export const GroupLayoutRenderer = (props: LayoutProps) => {
  const { data: _data, ...otherProps } = props;
  // We don't hand over data to the layout renderer to avoid rerendering it with every data change
  return <GroupLayoutRendererComponent {...otherProps} />;
};

const GroupLayoutRendererComponent: FunctionComponent<LayoutProps> = React.memo(
  function GroupLayoutRendererComponent({
    schema,
    uischema,
    path,
    enabled,
    visible,
    label,
  }: LayoutProps) {
    const group = uischema as GroupLayout;
    const options = (group.options || {}) as GroupLayoutOptions;
    const { collapsible = false, defaultExpanded = false } = options;

    const isHidden =
      visible === undefined || visible === null ? false : !visible;

    // Render as collapsible accordion
    if (collapsible) {
      const itemValue = `group-${label || path || "default"}`;

      return (
        <Accordion
          type="single"
          collapsible
          defaultValue={defaultExpanded ? itemValue : undefined}
          hidden={isHidden}
        >
          <AccordionItem value={itemValue} className="border-none">
            {!isEmpty(label) && (
              <AccordionTrigger className="text-xl py-2 hover:no-underline">
                {label}
              </AccordionTrigger>
            )}
            <AccordionContent className="pt-2">
              {renderChildren(group, schema, "mb-4", path, enabled)}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    }

    // Render as regular fieldset
    return (
      <fieldset hidden={isHidden}>
        {!isEmpty(label) ? (
          <legend className="text-xl mb-4">{label}</legend>
        ) : (
          ""
        )}
        {renderChildren(group, schema, "mb-4", path, enabled)}
      </fieldset>
    );
  },
);

export default withJsonFormsLayoutProps(GroupLayoutRenderer);
